import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { LinearProgress } from "@material-ui/core";
import { API_URL1 } from "../services/url";

// Per-load times now come from the backend (real Google Distance Matrix drive
// times + live traffic), returned as l.eta_label / l.cycle_min. The constants
// below are only a last-resort fallback if the server sent no ETA for a row.
const EST_START_HOUR = 7; // 7:00 AM
const EST_STEP_MIN = 90;
const TZ = "America/Phoenix";
const RED = "#991C1C";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const fallbackTimeForIndex = (i) =>
  moment().startOf("day").add(EST_START_HOUR, "hours").add(i * EST_STEP_MIN, "minutes").format("h:mm A");

const fmtCycle = (min) => {
  if (min == null || isNaN(min)) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h ${m}m cycle` : `${m}m cycle`;
};

const ScheduleBoard = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totals, setTotals] = useState({ totalLoads: 0, drivers: 0 });
  const [day, setDay] = useState("");
  // Native HTML5 drag state: which driver + which row is being dragged.
  const dragRef = useRef(null);

  const fetchLoads = useCallback(async (selDay) => {
    setLoading(true);
    try {
      const truck_company_id = localStorage.getItem("adminid");
      const q = selDay ? `?day=${selDay}&_ts=${Date.now()}` : `?_ts=${Date.now()}`;
      const resp = await axios.get(
        `${API_URL1}trucker/dashboardNew/schedule/${truck_company_id}${q}`,
        {
          headers: {
            "x-access-token": localStorage.getItem("TruckertokenKey"),
            "content-type": "application/json",
          },
        }
      );
      setLoads(Array.isArray(resp.data?.loads) ? resp.data.loads : []);
      setTotals({
        totalLoads: resp.data?.totalLoads || 0,
        drivers: resp.data?.drivers || 0,
      });
    } catch (e) {
      console.warn("ScheduleBoard: fetch failed", e);
      setLoads([]);
      setTotals({ totalLoads: 0, drivers: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoads(day);
  }, [day, fetchLoads]);

  // Group by driver. IMPORTANT: preserve server order (route_seq applied
  // server-side) — do NOT re-sort, or drag order would be lost on render.
  const byDriver = {};
  const driverOrder = [];
  loads.forEach((l) => {
    const key = l.driver_id || l.driver_name || "unassigned";
    if (!byDriver[key]) {
      byDriver[key] = {
        driver_id: l.driver_id,
        driver_name: l.driver_name || "Unassigned",
        phone_no: l.phone_no,
        truck: l.truck,
        loads: [],
      };
      driverOrder.push(key);
    }
    byDriver[key].loads.push(l);
  });
  const drivers = driverOrder.map((k) => byDriver[k]);

  const qty = (l) => Number(l.assign_qty || l.processing_quantity || 0);

  // Persist a driver's new order to the backend, then re-fetch so ETAs recompute.
  const persistOrder = async (driverKey, newDriverLoads) => {
    const ids = newDriverLoads
      .map((l) => parseInt(l.sub_job_truck_id, 10))
      .filter((n) => !Number.isNaN(n));
    if (ids.length === 0) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL1}trucker/dashboardNew/schedule/reorder`,
        { order: ids },
        {
          headers: {
            "x-access-token": localStorage.getItem("TruckertokenKey"),
            "content-type": "application/json",
          },
        }
      );
      // Re-fetch: drive legs changed, so server ETAs need recomputing.
      await fetchLoads(day);
    } catch (e) {
      console.warn("ScheduleBoard: reorder save failed", e);
      // Roll back to server truth on failure.
      await fetchLoads(day);
    } finally {
      setSaving(false);
    }
  };

  // Drop handler: move the dragged row to dropIndex within the SAME driver.
  const handleDrop = (driverKey, dropIndex) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag || drag.driverKey !== driverKey) return; // scoped per driver
    if (drag.index === dropIndex) return;

    const group = byDriver[driverKey];
    if (!group) return;
    const reordered = [...group.loads];
    const [moved] = reordered.splice(drag.index, 1);
    reordered.splice(dropIndex, 0, moved);

    // Optimistic: rebuild the flat loads array, keeping other drivers intact.
    const next = [];
    driverOrder.forEach((k) => {
      next.push(...(k === driverKey ? reordered : byDriver[k].loads));
    });
    setLoads(next);
    persistOrder(driverKey, reordered);
  };

  const displayDay = day || moment.tz(TZ).format("YYYY-MM-DD");
  const shiftDay = (delta) =>
    setDay(moment(displayDay, "YYYY-MM-DD").add(delta, "days").format("YYYY-MM-DD"));

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div style={{ padding: "24px 24px 8px 24px" }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#0f172a" }}>
          Driver Schedule
        </h1>
        <p style={{ margin: "6px 0 0 0", color: "#64748b", fontSize: 14 }}>
          Drag loads to set each driver's run order. Times (~) are estimated from
          real drive distance + live traffic — not committed appointment times.
        </p>
      </div>

      <div style={{ padding: "8px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => shiftDay(-1)} style={navBtn}>‹ Prev</button>
        <input
          type="date"
          value={displayDay}
          onChange={(e) => setDay(e.target.value)}
          style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #e5e7eb", fontWeight: 700, color: "#0f172a" }}
        />
        <button onClick={() => shiftDay(1)} style={navBtn}>Next ›</button>
        <button
          onClick={() => setDay("")}
          style={{ ...navBtn, background: day === "" ? RED : "#fff", color: day === "" ? "#fff" : RED }}
        >Today</button>
        {saving ? <span style={{ fontSize: 12, color: RED, fontWeight: 700 }}>Saving order…</span> : null}
        <div style={{ marginLeft: "auto", fontSize: 13, color: "#64748b", fontWeight: 700 }}>
          {day === "" ? "Today" : moment(displayDay, "YYYY-MM-DD").format("ddd, MMM D, YYYY")} · {totals.totalLoads} loads · {totals.drivers} drivers
        </div>
      </div>

      {loading ? <LinearProgress /> : null}

      <div style={{ padding: "8px 16px 32px 16px", display: "flex", flexWrap: "wrap", alignItems: "flex-start" }}>
        {!loading && drivers.length === 0 ? (
          <div style={{ color: "#94a3b8", fontSize: 14, padding: 24 }}>
            No delivery loads scheduled for this day.
          </div>
        ) : null}

        {drivers.map((g) => {
          const driverKey = g.driver_id || g.driver_name || "unassigned";
          const totalTons = g.loads.reduce((s, l) => s + qty(l), 0);
          return (
            <div key={driverKey} style={{
              flex: "1 1 360px", minWidth: 340, maxWidth: 460, margin: 8,
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
              boxShadow: "0 8px 16px rgba(2,6,23,0.06)", overflow: "hidden",
            }}>
              <div style={{ background: `linear-gradient(135deg, ${RED}, #6b1212)`, color: "#fff", padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ fontWeight: 800, fontSize: 16 }}>{g.driver_name}</div>
                  <div style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, opacity: 0.9 }}>
                    {g.loads.length} loads · {totalTons.toFixed(1)} t
                  </div>
                </div>
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                  {g.truck ? `Truck ${g.truck}` : "No truck"}{g.phone_no ? ` · ${g.phone_no}` : ""}
                </div>
              </div>

              <div style={{ padding: 12 }}>
                {g.loads.map((l, i) => {
                  const etaLabel = l.eta_label || `~${fallbackTimeForIndex(i)}`;
                  const cycle = fmtCycle(l.cycle_min);
                  return (
                    <div
                      key={l.sub_job_truck_id || i}
                      draggable
                      onDragStart={() => { dragRef.current = { driverKey, index: i }; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handleDrop(driverKey, i); }}
                      style={{
                        display: "flex", gap: 12, padding: "10px 8px",
                        borderBottom: i < g.loads.length - 1 ? "1px solid #f1f5f9" : "none",
                        cursor: "grab", background: "#fff",
                      }}
                      title="Drag to reorder this driver's route"
                    >
                      <div style={{ minWidth: 64, textAlign: "center" }}>
                        <div style={{
                          width: 30, height: 30, lineHeight: "30px", borderRadius: "50%",
                          background: "#fef2f2", color: RED, fontWeight: 800, fontSize: 13, margin: "0 auto",
                        }}>{i + 1}</div>
                        <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, marginTop: 4 }}>{ordinal(i + 1)}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>~{etaLabel.replace(/^~/, "")}</div>
                        {cycle ? <div style={{ fontSize: 10, color: "#cbd5e1", marginTop: 1 }}>{cycle}</div> : null}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>{l.customer_name || "—"}</div>
                        <div style={{ fontSize: 12, color: "#475569" }}>
                          {l.customer_site_name || ""}{l.site_city ? `, ${l.site_city}` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          <strong>{l.item_name || "Material"}</strong> · {qty(l).toFixed(1)} t
                        </div>
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                          Pickup: {l.quarry_site_name || "—"}{l.no_of_trips > 1 ? ` · ${l.no_of_trips} trips` : ""}
                          {l.mssql_order_id ? ` · ${l.mssql_order_id}` : ""}
                        </div>
                      </div>
                      <div style={{ alignSelf: "center", color: "#cbd5e1", fontSize: 18, userSelect: "none" }}>⠿</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const navBtn = {
  padding: "8px 14px", borderRadius: 8, border: `1px solid ${RED}`,
  background: "#fff", color: RED, fontWeight: 700, cursor: "pointer",
};

export default ScheduleBoard;
